import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
interface AnalogClockProps {
  hours: number;
  minutes: number;
  seconds?: number;
  size?: number;
  showSeconds?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
  className?: string;
  /** Enables drag/click-to-set interaction. Defaults to false. */
  interactive?: boolean;
  /** Which hand the user is currently setting. Required when interactive. */
  activeMode?: 'hours' | 'minutes';
  /** Snap step for minutes (1 = continuous, 5 = quarter-style). Default 1. */
  minuteStep?: 1 | 5;
  /** Called whenever user updates the time via drag/click/keyboard.
   *  Hours are always reported in 12-hour range (0-11) when interactive. */
  onChange?: (hours: number, minutes: number) => void;
  /** Show small 5-minute labels (5, 10, 15...) outside the hours. Default true. */
  showFiveMinuteLabels?: boolean;
}
type DragState = {
  pointerId: number;
  startedAt: number;
  movedSinceDown: boolean;
};
const HAPTIC_THRESHOLD = 50; // ms between haptic pulses
export function AnalogClock({
  hours,
  minutes,
  seconds = 0,
  size: propSize,
  showSeconds = false,
  label,
  mode = 'support',
  className = '',
  interactive = false,
  activeMode = 'minutes',
  minuteStep = 1,
  onChange,
  showFiveMinuteLabels = true
}: AnalogClockProps) {
  const size = propSize ?? (mode === 'stimulus' ? 120 : 160);
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const lastHapticRef = useRef<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const triggerHaptic = useCallback(() => {
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
    const now = performance.now();
    if (now - lastHapticRef.current < HAPTIC_THRESHOLD) return;
    lastHapticRef.current = now;
    try {
      navigator.vibrate(8);
    } catch {
      /* noop */
    }
  }, []);
  /** Compute new time from pointer coordinates (client space). 12-hour domain. */
  const computeTimeFromPoint = useCallback((clientX: number, clientY: number): {
    h: number;
    m: number;
  } | null => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    // Angle in degrees, 0 at 12, clockwise positive
    let angle = Math.atan2(dx, -dy) * (180 / Math.PI);
    if (angle < 0) angle += 360;
    if (activeMode === 'minutes') {
      let m = Math.round(angle / 6) % 60;
      if (minuteStep === 5) {
        m = Math.round(m / 5) * 5 % 60;
      }
      return {
        h: hours,
        m
      };
    } else {
      // Hours in 12-hour mode: each hour = 30deg.
      // Domain returned: 0-11 (where 12-o'clock = 0).
      const h = Math.round(angle / 30) % 12;
      return {
        h,
        m: minutes
      };
    }
  }, [activeMode, hours, minutes, minuteStep]);
  const applyUpdate = useCallback((clientX: number, clientY: number) => {
    const next = computeTimeFromPoint(clientX, clientY);
    if (!next) return;
    if (next.h !== hours || next.m !== minutes) {
      triggerHaptic();
      onChange?.(next.h, next.m);
    }
  }, [computeTimeFromPoint, hours, minutes, onChange, triggerHaptic]);
  const handlePointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive) return;
    e.preventDefault();
    dragRef.current = {
      pointerId: e.pointerId,
      startedAt: performance.now(),
      movedSinceDown: false
    };
    setIsDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }, [interactive]);
  const handlePointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive || !dragRef.current) return;
    if (dragRef.current.pointerId !== e.pointerId) return;
    dragRef.current.movedSinceDown = true;
    applyUpdate(e.clientX, e.clientY);
  }, [interactive, applyUpdate]);
  const handlePointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive || !dragRef.current) return;
    if (dragRef.current.pointerId !== e.pointerId) return;
    // Tap (no movement) → snap to that position
    if (!dragRef.current.movedSinceDown) {
      applyUpdate(e.clientX, e.clientY);
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    dragRef.current = null;
    setIsDragging(false);
  }, [interactive, applyUpdate]);
  // Keyboard support
  useEffect(() => {
    if (!interactive) return;
    const el = wrapperRef.current;
    if (!el) return;
    const handleKey = (e: KeyboardEvent) => {
      if (document.activeElement !== el) return;
      const big = e.shiftKey;
      if (activeMode === 'minutes') {
        const step = big ? 5 : minuteStep;
        if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
          e.preventDefault();
          onChange?.(hours, (minutes + step) % 60);
          triggerHaptic();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
          e.preventDefault();
          onChange?.(hours, (minutes - step + 60) % 60);
          triggerHaptic();
        }
      } else {
        const step = big ? 3 : 1;
        if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
          e.preventDefault();
          onChange?.((hours + step) % 12, minutes);
          triggerHaptic();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
          e.preventDefault();
          onChange?.((hours - step + 12) % 12, minutes);
          triggerHaptic();
        }
      }
    };
    el.addEventListener('keydown', handleKey);
    return () => el.removeEventListener('keydown', handleKey);
  }, [interactive, activeMode, hours, minutes, minuteStep, onChange, triggerHaptic]);
  // Angles — in display we always use 12-hour position regardless of value
  const hourAngle = hours % 12 * 30 + minutes / 60 * 30;
  const minuteAngle = minutes * 6 + seconds / 60 * 6;
  const secondAngle = seconds * 6;
  const hoursActive = interactive && activeMode === 'hours';
  const minutesActive = interactive && activeMode === 'minutes';
  const hourColor = hoursActive ? '#6366f1' : interactive ? '#cbd5e1' : '#1e293b';
  const minuteColor = minutesActive ? '#6366f1' : interactive ? '#cbd5e1' : '#475569';
  const hourWidth = hoursActive ? 5.5 : 4.5;
  const minuteWidth = minutesActive ? 4 : 3;
  return <motion.div ref={wrapperRef} tabIndex={interactive ? 0 : -1} initial={{
    opacity: 0,
    scale: 0.92
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex flex-col items-center gap-3 outline-none ${interactive ? 'focus-visible:ring-4 focus-visible:ring-indigo-300 rounded-full' : ''} ${className}`} data-id="element-11718">
      <div className={`relative rounded-full bg-white shadow-2xl transition-shadow ${interactive ? 'cursor-pointer touch-none select-none' : ''} ${isDragging ? 'shadow-indigo-300/60' : ''}`} style={{
      width: size,
      height: size
    }} data-id="element-11719">
        <svg ref={svgRef} viewBox="0 0 100 100" className="w-full h-full overflow-visible" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} role={interactive ? 'slider' : 'img'} aria-label={interactive ? `Klok instellen. Sleep om aan te passen.` : `Klok wijst ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} aan.`} data-id="element-11720">
          <defs data-id="element-11721">
            <linearGradient id="clockBg" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-11722">
              <stop offset="0%" stopColor="#ffffff" data-id="element-11723" />
              <stop offset="100%" stopColor="#f8fafc" data-id="element-11724" />
            </linearGradient>
            <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-11725">
              <feDropShadow dx="0" dy="0.5" stdDeviation="1" floodOpacity="0.18" data-id="element-11726" />
            </filter>
          </defs>

          {/* Background */}
          <circle cx="50" cy="50" r="48" fill="url(#clockBg)" stroke="#e2e8f0" strokeWidth="2" data-id="element-11727" />
          <circle cx="50" cy="50" r="45.5" fill="none" stroke="#f1f5f9" strokeWidth="1" data-id="element-11728" />

          {/* Minute ticks (light) */}
          {[...Array(60)].map((_, i) => {
          if (i % 5 === 0) return null;
          return <line key={`m-${i}`} x1="50" y1="6" x2="50" y2="8.5" stroke="#cbd5e1" strokeWidth="0.6" strokeLinecap="round" transform={`rotate(${i * 6} 50 50)`} data-id="element-11729" />;
        })}

          {/* Hour ticks (bolder) */}
          {[...Array(12)].map((_, i) => <line key={`h-${i}`} x1="50" y1="4.5" x2="50" y2="9.5" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" transform={`rotate(${i * 30} 50 50)`} data-id="element-11730" />)}

          {/* Numbers 1-12 */}
          {[...Array(12)].map((_, i) => {
          const num = i + 1;
          const rad = (num * 30 - 90) * (Math.PI / 180);
          const r = 33;
          const x = 50 + r * Math.cos(rad);
          const y = 50 + r * Math.sin(rad);
          return <text key={`num-${num}`} x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="#334155" fontSize="10.5" fontWeight="800" className="select-none pointer-events-none" style={{
            fontFamily: 'system-ui, sans-serif'
          }} data-id="element-11731">
                {num}
              </text>;
        })}

          {/* 5-minute labels (outer ring) */}
          {showFiveMinuteLabels && [...Array(12)].map((_, i) => {
          const num = i === 0 ? 60 : i * 5;
          const rad = (i * 30 - 90) * (Math.PI / 180);
          const r = 42.5; // Outside the hour numbers
          const x = 50 + r * Math.cos(rad);
          const y = 50 + r * Math.sin(rad);
          return <text key={`min-${num}`} x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="#94a3b8" fontSize="5" fontWeight="600" className="select-none pointer-events-none" style={{
            fontFamily: 'system-ui, sans-serif'
          }} data-id="element-11732">
                  {num}
                </text>;
        })}

          {/* Active drag-zone hint ring */}
          {interactive && <circle cx="50" cy="50" r="44" fill="none" stroke={isDragging ? '#6366f1' : 'transparent'} strokeWidth="0.8" strokeDasharray="2 2" opacity={isDragging ? 0.5 : 0} style={{
          transition: 'opacity 0.2s'
        }} data-id="element-11733" />}

          {/* HOUR HAND */}
          <motion.g animate={{
          rotate: hourAngle
        }} initial={false} transition={isDragging ? {
          duration: 0
        } : {
          type: 'spring',
          stiffness: 220,
          damping: 22
        }} style={{
          originX: '50px',
          originY: '50px'
        }} filter="url(#dropShadow)" data-id="element-11734">
            <rect x={50 - hourWidth / 2} y="26" width={hourWidth} height="26" rx={hourWidth / 2} fill={hourColor} style={{
            transition: 'fill 0.25s'
          }} data-id="element-11735" />
            {hoursActive && <>
                <circle cx="50" cy="28" r="3.8" fill="#6366f1" stroke="#ffffff" strokeWidth="1.4" data-id="element-11736" />
                <motion.circle cx="50" cy="28" r="3.8" fill="none" stroke="#6366f1" strokeWidth="1" animate={{
              r: [3.8, 7, 3.8],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 1.8,
              repeat: Infinity
            }} data-id="element-11737" />
              </>}
          </motion.g>

          {/* MINUTE HAND */}
          <motion.g animate={{
          rotate: minuteAngle
        }} initial={false} transition={isDragging ? {
          duration: 0
        } : {
          type: 'spring',
          stiffness: 260,
          damping: 24
        }} style={{
          originX: '50px',
          originY: '50px'
        }} filter="url(#dropShadow)" data-id="element-11738">
            <rect x={50 - minuteWidth / 2} y="14" width={minuteWidth} height="38" rx={minuteWidth / 2} fill={minuteColor} style={{
            transition: 'fill 0.25s'
          }} data-id="element-11739" />
            {minutesActive && <>
                <circle cx="50" cy="16" r="3.8" fill="#6366f1" stroke="#ffffff" strokeWidth="1.4" data-id="element-11740" />
                <motion.circle cx="50" cy="16" r="3.8" fill="none" stroke="#6366f1" strokeWidth="1" animate={{
              r: [3.8, 7, 3.8],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 1.8,
              repeat: Infinity
            }} data-id="element-11741" />
              </>}
          </motion.g>

          {/* SECOND HAND — only in display mode */}
          {showSeconds && !interactive && <motion.g animate={{
          rotate: secondAngle
        }} initial={false} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} style={{
          originX: '50px',
          originY: '50px'
        }} data-id="element-11742">
              <rect x="49.4" y="12" width="1.2" height="42" rx="0.6" fill="#ef4444" data-id="element-11743" />
              <circle cx="50" cy="50" r="2" fill="#ef4444" data-id="element-11744" />
            </motion.g>}

          {/* Center dot */}
          <circle cx="50" cy="50" r="3" fill="#1e293b" stroke="#ffffff" strokeWidth="1" data-id="element-11745" />
        </svg>
      </div>

      {label && <p className={`font-semibold text-slate-600 text-center ${mode === 'stimulus' ? 'text-xs' : 'text-sm'}`} data-id="element-11746">
          {label}
        </p>}
    </motion.div>;
}